import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useTicketMarkReadStatus, useTicketMarkUnReadStatus } from 'hooks/api/useTickets';

import useTicketsRefinement from '../../TicketsRefinement/hooks/useTicketsRefinement';

interface TicketsReadStatusHookProps {
  selectedTickets: string[];
  setSelectedTickets: (value: string[]) => void;
}

interface TicketsReadStatusHookResult {
  errorIsVisible: boolean;
  selectedOption: SelectOptionT;
  readStatusTicketErrorMessage: string | undefined;
  unreadStatusTicketErrorMessage: string | undefined;
  handlePopupOpen: () => void;
  handlePopupClose: () => void;
  handleOptionChange: (newValue: SelectOptionT | null) => SelectOptionT | null;
  isPopupOpen: boolean;
}

function useTicketsReadStatus({
  selectedTickets,
  setSelectedTickets,
}: TicketsReadStatusHookProps): TicketsReadStatusHookResult {
  const queryClient = useQueryClient();
  const [IsPopupOpen, setPopupVisibility] = useState<boolean>(false);
  const customDefaultValue: SelectOptionT = { value: '', label: '' };
  const [selectedOption, setSelectedOption] = useState<SelectOptionT>(customDefaultValue);
  const { setIsSelectAllTicketsChecked } = useTicketsRefinement();
  const { selectedTicketId } = useTickets();

  const markStatusAsReadMutation = useTicketMarkReadStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      setIsSelectAllTicketsChecked(false);
      setSelectedTickets([]);
    },
  });

  const markStatusAsUnReadMutation = useTicketMarkUnReadStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      setIsSelectAllTicketsChecked(false);
      setSelectedTickets([]);
    },
  });

  const readStatusTicketErrorMessage = useMemo(() => {
    setPopupVisibility(true);
    return getValidationErrorMessage(markStatusAsReadMutation.error);
  }, [markStatusAsReadMutation.error]);

  const unreadStatusTicketErrorMessage = useMemo(() => {
    setPopupVisibility(true);

    return getErrorMessage(markStatusAsUnReadMutation.error);
  }, [markStatusAsUnReadMutation.error]);

  /**
   * Handlers
   */
  const handleOptionSelection = useCallback(
    (selectedOption: SelectOptionT) => {
      if (selectedOption.value === 'read') {
        markStatusAsReadMutation.mutate({
          ticketIds: selectedTickets,
        });
      } else if (selectedOption.value === 'unread') {
        markStatusAsUnReadMutation.mutate({ ticketIds: selectedTickets });
      }
    },
    [markStatusAsReadMutation, markStatusAsUnReadMutation, selectedTickets]
  );

  const handlePopupOpen = (): void => {
    setPopupVisibility(true);
  };

  const handlePopupClose = (): void => {
    setPopupVisibility(false);
  };

  const handleOptionChange = (newValue: SelectOptionT | null): SelectOptionT | null => {
    setSelectedOption(newValue || customDefaultValue);
    if (newValue !== null) {
      handleOptionSelection(newValue);
    }
    return newValue;
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (selectedTicketId) {
      markStatusAsReadMutation.mutate({
        ticketIds: [selectedTicketId],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId]); //To automatically move a ticket to the "read" section of the list after its details are opened

  return {
    errorIsVisible: markStatusAsReadMutation.isError || markStatusAsUnReadMutation.isError,
    selectedOption,
    isPopupOpen: IsPopupOpen,
    readStatusTicketErrorMessage,
    unreadStatusTicketErrorMessage,
    handlePopupOpen,
    handlePopupClose,
    handleOptionChange,
  };
}

export default useTicketsReadStatus;
